import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  ExternalLink,
  AlertTriangle,
  Bell,
  Hash,
  FlaskConical,
} from 'lucide-react'
import {
  getOrganization,
  updateOrganization,
  updateSlackCredentials,
  removeSlackCredentials,
  testSlackConnection,
  listSlackChannels,
  getSlackInstallUrl,
  type SlackCredentialsUpdate,
} from '@/api/organizations'
import { useAuthStore } from '@/stores/authStore'
import { updateUser, listUsers, setTestUsers } from '@/api/users'

export default function Settings() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  const isAdminUser = user?.role === 'admin'

  // Organization data — only fetch for admins
  const { data: org, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const data = await getOrganization()
      return data
    },
    enabled: isAdminUser,
  })

  // Organization name form
  const [orgName, setOrgName] = useState('')
  const [isEditingName, setIsEditingName] = useState(false)

  // Slack credentials form
  const [slackForm, setSlackForm] = useState<SlackCredentialsUpdate>({
    slack_bot_token: '',
    slack_signing_secret: '',
    slack_client_id: '',
    slack_client_secret: '',
  })
  const [showBotToken, setShowBotToken] = useState(false)
  const [showSigningSecret, setShowSigningSecret] = useState(false)
  const [showClientSecret, setShowClientSecret] = useState(false)

  // Test result
  const [testResult, setTestResult] = useState<{
    success: boolean
    message: string
  } | null>(null)

  // Confirmation modal
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)

  // Password change form (self)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordChangeMsg, setPasswordChangeMsg] = useState<string | null>(null)

  // Notifications form (admin only) — channel inputs hold the channel name (or raw ID)
  const [attendanceChannelInput, setAttendanceChannelInput] = useState('')
  const [leaveChannelInput, setLeaveChannelInput] = useState('')
  const [goalsChannelInput, setGoalsChannelInput] = useState('')
  const [checkinRemindersEnabled, setCheckinRemindersEnabled] = useState(true)
  const [notifyMsg, setNotifyMsg] = useState<{ success: boolean; message: string } | null>(null)

  // Test mode state
  const [testModeEnabled, setTestModeEnabled] = useState(false)
  const [testAttendanceInput, setTestAttendanceInput] = useState('')
  const [testLeaveInput, setTestLeaveInput] = useState('')
  const [testGoalsInput, setTestGoalsInput] = useState('')
  const [testMsg, setTestMsg] = useState<{ success: boolean; message: string } | null>(null)
  const [testUserIds, setTestUserIds] = useState<Set<string>>(new Set())

  // Fetch all users for test-user selector (admin only)
  const { data: allUsers } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => listUsers({ per_page: 500 }),
    enabled: isAdminUser,
    staleTime: 60 * 1000,
  })

  useEffect(() => {
    if (org) {
      setOrgName(org.name)
      setCheckinRemindersEnabled(org.checkin_reminders_enabled)
      setTestModeEnabled(org.test_mode_enabled)
    }
  }, [org])

  // Hydrate test user selection from fetched users
  const [testUsersHydrated, setTestUsersHydrated] = useState(false)
  useEffect(() => {
    if (testUsersHydrated || !allUsers) return
    const ids = new Set<string>()
    for (const u of allUsers.items) {
      if (u.is_test_user) ids.add(u.id)
    }
    setTestUserIds(ids)
    setTestUsersHydrated(true)
  }, [allUsers, testUsersHydrated])

  // Slack channels list (only when Slack connected)
  const { data: slackChannels, isLoading: channelsLoading } = useQuery({
    queryKey: ['slack-channels'],
    queryFn: listSlackChannels,
    enabled: Boolean(isAdminUser && org?.slack_connected),
    staleTime: 60 * 1000,
  })

  // Once both org + channels are loaded, hydrate the inputs with channel names
  // (looking up the stored IDs). Falls back to the raw ID if the bot can't see
  // the channel. Only runs on initial load of either source, so user edits
  // are not overwritten.
  const [inputsHydrated, setInputsHydrated] = useState(false)
  useEffect(() => {
    if (inputsHydrated || !org) return
    const resolveName = (id: string | null): string => {
      if (!id) return ''
      const match = slackChannels?.find((c) => c.id === id)
      return match ? match.name : id
    }
    // Wait for channels if Slack is connected (so we can resolve names)
    if (org.slack_connected && !slackChannels) return
    setAttendanceChannelInput(resolveName(org.attendance_channel_id))
    setLeaveChannelInput(resolveName(org.leave_channel_id))
    setGoalsChannelInput(resolveName(org.goals_channel_id))
    setTestAttendanceInput(resolveName(org.test_attendance_channel_id))
    setTestLeaveInput(resolveName(org.test_leave_channel_id))
    setTestGoalsInput(resolveName(org.test_goals_channel_id))
    setInputsHydrated(true)
  }, [org, slackChannels, inputsHydrated])

  // Resolve a user-typed channel input to an ID that can be saved.
  // Accepts: channel name ("general", "#general"), or raw Slack ID (C..., G...).
  // Returns { id: string | null, error: string | null }.
  const resolveChannelInput = (input: string): { id: string | null; error: string | null } => {
    const trimmed = input.trim().replace(/^#/, '')
    if (!trimmed) return { id: null, error: null }
    // Raw Slack channel ID
    if (/^[CG][A-Z0-9]{8,}$/.test(trimmed)) {
      return { id: trimmed, error: null }
    }
    // Match by name (case-insensitive)
    const match = slackChannels?.find(
      (c) => c.name.toLowerCase() === trimmed.toLowerCase()
    )
    if (match) return { id: match.id, error: null }
    return {
      id: null,
      error: `Channel "#${trimmed}" not found. Check the name or invite the bot to the channel.`,
    }
  }

  // Mutations
  const updateOrgMutation = useMutation({
    mutationFn: updateOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] })
      setIsEditingName(false)
    },
  })

  const updateNotifySettingsMutation = useMutation({
    mutationFn: updateOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] })
      setNotifyMsg({ success: true, message: 'Notification settings saved.' })
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } }; message?: string }
      setNotifyMsg({
        success: false,
        message: axiosError.response?.data?.detail || axiosError.message || 'Failed to save settings',
      })
    },
  })

  const updateTestSettingsMutation = useMutation({
    mutationFn: updateOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] })
      setTestMsg({ success: true, message: 'Test mode settings saved.' })
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } }; message?: string }
      setTestMsg({
        success: false,
        message: axiosError.response?.data?.detail || axiosError.message || 'Failed to save test settings',
      })
    },
  })

  const updateSlackMutation = useMutation({
    mutationFn: updateSlackCredentials,
    onSuccess: (data) => {
      console.log('Slack credentials saved, response:', data)
      queryClient.invalidateQueries({ queryKey: ['organization'] })
      setSlackForm({
        slack_bot_token: '',
        slack_signing_secret: '',
        slack_client_id: '',
        slack_client_secret: '',
      })
      setTestResult({
        success: true,
        message: data.slack_connected
          ? `Slack credentials saved! Connected to: ${data.slack_team_name || 'workspace'}`
          : 'Credentials saved but connection not verified. Click "Test Connection" to verify.',
      })
    },
    onError: (error: unknown) => {
      console.error('Failed to save Slack credentials:', error)
      const axiosError = error as { response?: { data?: { detail?: string } }; message?: string }
      const message = axiosError.response?.data?.detail || axiosError.message || 'Failed to save credentials'
      setTestResult({
        success: false,
        message,
      })
    },
  })

  const testConnectionMutation = useMutation({
    mutationFn: testSlackConnection,
    onSuccess: (result) => {
      console.log('Test connection result:', result)
      if (result.success) {
        setTestResult({
          success: true,
          message: `Connected to workspace: ${result.team_name}`,
        })
        queryClient.invalidateQueries({ queryKey: ['organization'] })
      } else {
        setTestResult({
          success: false,
          message: result.error || 'Connection test failed',
        })
      }
    },
    onError: (error: unknown) => {
      console.error('Test connection failed:', error)
      const axiosError = error as { response?: { data?: { detail?: string } }; message?: string }
      const message = axiosError.response?.data?.detail || axiosError.message || 'Connection test failed'
      setTestResult({
        success: false,
        message,
      })
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: removeSlackCredentials,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] })
      setShowDisconnectConfirm(false)
      setTestResult({
        success: true,
        message: 'Slack disconnected successfully',
      })
    },
  })

  const passwordMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated')
      if (!newPassword || newPassword.length < 8) throw new Error('Password must be at least 8 characters')
      if (newPassword !== confirmPassword) throw new Error('Passwords do not match')
      await updateUser(user.id, { password: newPassword })
    },
    onSuccess: () => {
      setPasswordChangeMsg('Password updated successfully!')
      setNewPassword('')
      setConfirmPassword('')
    },
    onError: (error: any) => {
      setPasswordChangeMsg(error?.response?.data?.detail || error?.message || 'Failed to update password')
    },
  })

  const handleSaveOrgName = () => {
    if (orgName.trim() && orgName !== org?.name) {
      updateOrgMutation.mutate({ name: orgName.trim() })
    } else {
      setIsEditingName(false)
    }
  }

  const handleSaveSlackCredentials = (e: React.FormEvent) => {
    e.preventDefault()

    if (!org?.slack_connected) {
      if (!slackForm.slack_bot_token || !slackForm.slack_signing_secret) {
        setTestResult({
          success: false,
          message: 'Bot Token and Signing Secret are required',
        })
        return
      }
    } else {
      if (!slackForm.slack_bot_token && !slackForm.slack_signing_secret &&
          !slackForm.slack_client_id && !slackForm.slack_client_secret) {
        setTestResult({
          success: false,
          message: 'Enter at least one credential to update',
        })
        return
      }
    }

    const payload: SlackCredentialsUpdate = {
      slack_bot_token: slackForm.slack_bot_token || '',
      slack_signing_secret: slackForm.slack_signing_secret || '',
    }

    if (slackForm.slack_client_id) {
      payload.slack_client_id = slackForm.slack_client_id
    }
    if (slackForm.slack_client_secret) {
      payload.slack_client_secret = slackForm.slack_client_secret
    }

    updateSlackMutation.mutate(payload)
  }

  if (isAdminUser && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    )
  }

  const isAdmin = isAdminUser

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
        <p className="text-gray-500 mt-1">Manage your organization and integrations</p>
      </div>

      {/* Organization Settings — Admin only */}
      {isAdmin && <><div className="card">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand/10 rounded-lg">
              <Building2 className="h-5 w-5 text-brand" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Organization
              </h2>
              <p className="text-sm text-gray-500">
                Basic organization information
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization Name
            </label>
            {isEditingName ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  autoFocus
                />
                <button
                  onClick={handleSaveOrgName}
                  disabled={updateOrgMutation.isPending}
                  className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark disabled:opacity-50"
                >
                  {updateOrgMutation.isPending ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingName(false)
                    setOrgName(org?.name || '')
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-gray-900">{org?.name}</span>
                <button
                  onClick={() => setIsEditingName(true)}
                  className="text-sm text-brand hover:text-brand-dark"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workspace Slug
            </label>
            <span className="text-gray-500">{org?.slug}</span>
          </div>
        </div>
      </div>

      {/* Slack Integration */}
      <div className="card">
        <div className="p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                <MessageSquare className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Slack Integration
                </h2>
                <p className="text-sm text-gray-500">
                  Connect your Slack workspace for standups and notifications
                </p>
              </div>
            </div>
            {org?.slack_connected && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm font-medium">Connected</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {org?.slack_connected ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-green-800">
                      Connected to Slack
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Workspace: <strong>{org.slack_team_name || 'Unknown'}</strong>
                      {org.slack_team_id && (
                        <span className="text-green-600 ml-2">
                          ({org.slack_team_id})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 ml-8 sm:ml-0">
                  <button
                    onClick={() => testConnectionMutation.mutate()}
                    disabled={testConnectionMutation.isPending}
                    className="px-3 py-1.5 text-sm bg-white border border-green-300 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-50"
                  >
                    {testConnectionMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Test'
                    )}
                  </button>
                  <button
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">
                    Slack not connected
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Connect your Slack workspace to enable standups and team
                    communication features.
                  </p>
                </div>
              </div>
            </div>
          )}

          {testResult && (
            <div
              className={`p-4 rounded-lg ${
                testResult.success
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}
            >
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span
                  className={
                    testResult.success ? 'text-green-800' : 'text-red-800'
                  }
                >
                  {testResult.message}
                </span>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">
              {org?.slack_connected ? 'Reinstall Managition App' : 'Install Managition App'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {org?.slack_connected
                ? 'Your workspace is connected. You can reinstall to refresh permissions or switch workspaces.'
                : 'Add Managition to your Slack workspace in one click. No manual credentials needed.'}
            </p>

            <button
              type="button"
              onClick={async () => {
                setTestResult(null)
                try {
                  const { url } = await getSlackInstallUrl()
                  window.location.href = url
                } catch (err: any) {
                  setTestResult({
                    success: false,
                    message: err?.response?.data?.detail || 'Unable to start Slack install',
                  })
                }
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#4A154B] text-white rounded-md font-medium hover:bg-[#611f63] transition-colors"
            >
              <svg className="h-4 w-4" viewBox="0 0 122.8 122.8" fill="currentColor">
                <path d="M25.8 77.6a12.9 12.9 0 01-12.9 12.9A12.9 12.9 0 010 77.6a12.9 12.9 0 0112.9-12.9h12.9v12.9zm6.5 0a12.9 12.9 0 0112.9-12.9 12.9 12.9 0 0112.9 12.9v32.3a12.9 12.9 0 01-12.9 12.9 12.9 12.9 0 01-12.9-12.9V77.6zM45.2 25.8a12.9 12.9 0 01-12.9-12.9A12.9 12.9 0 0145.2 0a12.9 12.9 0 0112.9 12.9v12.9H45.2zm0 6.5a12.9 12.9 0 0112.9 12.9 12.9 12.9 0 01-12.9 12.9H12.9A12.9 12.9 0 010 45.2a12.9 12.9 0 0112.9-12.9h32.3zM97 45.2a12.9 12.9 0 0112.9-12.9 12.9 12.9 0 0112.9 12.9 12.9 12.9 0 01-12.9 12.9H97V45.2zm-6.5 0a12.9 12.9 0 01-12.9 12.9 12.9 12.9 0 01-12.9-12.9V12.9A12.9 12.9 0 0177.6 0a12.9 12.9 0 0112.9 12.9v32.3zM77.6 97a12.9 12.9 0 0112.9 12.9 12.9 12.9 0 01-12.9 12.9 12.9 12.9 0 01-12.9-12.9V97h12.9zm0-6.5a12.9 12.9 0 01-12.9-12.9 12.9 12.9 0 0112.9-12.9h32.3a12.9 12.9 0 0112.9 12.9 12.9 12.9 0 01-12.9 12.9H77.6z"/>
              </svg>
              {org?.slack_connected ? 'Reinstall to Slack' : 'Add to Slack'}
            </button>
          </div>
        </div>
      </div>

      {/* Notifications & Channels */}
      <div className="card">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Bell className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
              <p className="text-sm text-gray-500">
                Configure channels and reminder preferences for your workspace
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {!org?.slack_connected && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
              Connect Slack above to select channels.
            </div>
          )}

          {/* Check-in reminders toggle */}
          <div className="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200">
            <div>
              <div className="font-medium text-gray-900">Check-in reminders</div>
              <p className="text-sm text-gray-500 mt-1">
                Send DM reminders to users on their shift days once their shift has started and they haven't checked in yet.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={checkinRemindersEnabled}
              onClick={() => setCheckinRemindersEnabled(!checkinRemindersEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                checkinRemindersEnabled ? 'bg-teal-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  checkinRemindersEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Channel name inputs (autocomplete via datalist) */}
          <datalist id="slack-channel-names">
            {slackChannels?.map((ch) => (
              <option key={ch.id} value={ch.name}>
                {ch.is_private ? '🔒 private' : 'public'}
                {!ch.is_member ? ' — bot will auto-join' : ''}
              </option>
            ))}
          </datalist>
          <div className="space-y-4">
            {(['attendance', 'leave', 'goals'] as const).map((key) => {
              const value = key === 'attendance' ? attendanceChannelInput
                : key === 'leave' ? leaveChannelInput : goalsChannelInput
              const setValue = key === 'attendance' ? setAttendanceChannelInput
                : key === 'leave' ? setLeaveChannelInput : setGoalsChannelInput
              const labels: Record<string, { title: string; hint: string }> = {
                attendance: { title: 'Attendance Channel', hint: 'Check-in / check-out announcements' },
                leave: { title: 'Leave Channel', hint: 'Leave / WFH / early-leave / late notices' },
                goals: { title: 'Goals Channel', hint: 'Daily goals & end-of-day summaries' },
              }
              return (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {labels[key].title}
                    <span className="ml-2 text-xs text-gray-400 font-normal">{labels[key].hint}</span>
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      list="slack-channel-names"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      disabled={!org?.slack_connected || channelsLoading}
                      placeholder={channelsLoading ? 'Loading channels...' : 'channel-name'}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand disabled:bg-gray-50 disabled:text-gray-400"
                      autoComplete="off"
                    />
                  </div>
                </div>
              )
            })}
          </div>

          {notifyMsg && (
            <div className={`p-3 rounded-lg text-sm ${notifyMsg.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              {notifyMsg.message}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setNotifyMsg(null)
                const att = resolveChannelInput(attendanceChannelInput)
                const lv = resolveChannelInput(leaveChannelInput)
                const gl = resolveChannelInput(goalsChannelInput)
                const firstError = att.error || lv.error || gl.error
                if (firstError) {
                  setNotifyMsg({ success: false, message: firstError })
                  return
                }
                updateNotifySettingsMutation.mutate({
                  attendance_channel_id: att.id,
                  leave_channel_id: lv.id,
                  goals_channel_id: gl.id,
                  checkin_reminders_enabled: checkinRemindersEnabled,
                })
              }}
              disabled={updateNotifySettingsMutation.isPending}
              className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 flex items-center gap-2"
            >
              {updateNotifySettingsMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Notification Settings
            </button>
          </div>
        </div>
      </div>
      {/* Testing */}
      <div className="card">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FlaskConical className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Testing</h2>
              <p className="text-sm text-gray-500">
                Route all notifications to test channels without changing production settings
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {/* Test mode toggle */}
          <div className="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200">
            <div>
              <div className="font-medium text-gray-900">Test Mode</div>
              <p className="text-sm text-gray-500 mt-1">
                When enabled, all Slack notifications are sent to the test channels below instead of the production channels.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={testModeEnabled}
              onClick={() => setTestModeEnabled(!testModeEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                testModeEnabled ? 'bg-amber-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  testModeEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {testModeEnabled && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              Test mode is active — all notifications will go to the test channels below.
            </div>
          )}

          {/* Test channel inputs */}
          <datalist id="slack-channel-names-test">
            {slackChannels?.map((ch) => (
              <option key={ch.id} value={ch.name}>
                {ch.is_private ? '🔒 private' : 'public'}
              </option>
            ))}
          </datalist>
          <div className="space-y-4">
            {(['attendance', 'leave', 'goals'] as const).map((key) => {
              const value = key === 'attendance' ? testAttendanceInput
                : key === 'leave' ? testLeaveInput : testGoalsInput
              const setValue = key === 'attendance' ? setTestAttendanceInput
                : key === 'leave' ? setTestLeaveInput : setTestGoalsInput
              const labels: Record<string, { title: string; hint: string }> = {
                attendance: { title: 'Test Attendance Channel', hint: 'Check-in / check-out test' },
                leave: { title: 'Test Leave Channel', hint: 'Leave notices test' },
                goals: { title: 'Test Goals Channel', hint: 'Goals summaries test' },
              }
              return (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {labels[key].title}
                    <span className="ml-2 text-xs text-gray-400 font-normal">{labels[key].hint}</span>
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      list="slack-channel-names-test"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      disabled={!org?.slack_connected || channelsLoading}
                      placeholder={channelsLoading ? 'Loading channels...' : 'test-channel-name'}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand disabled:bg-gray-50 disabled:text-gray-400"
                      autoComplete="off"
                    />
                  </div>
                </div>
              )
            })}
          </div>

          {/* Test users selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Test Users
              <span className="ml-2 text-xs text-gray-400 font-normal">Only these users will route to test channels</span>
            </label>
            <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
              {allUsers?.items.filter(u => u.is_active).map((u) => (
                <label key={u.id} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={testUserIds.has(u.id)}
                    onChange={() => {
                      const next = new Set(testUserIds)
                      if (next.has(u.id)) next.delete(u.id)
                      else next.add(u.id)
                      setTestUserIds(next)
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div className="flex items-center gap-2 min-w-0">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt="" className="h-6 w-6 rounded-full" />
                    ) : (
                      <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm text-gray-900 truncate">{u.name}</span>
                    <span className="text-xs text-gray-400 truncate">{u.email}</span>
                  </div>
                </label>
              ))}
              {(!allUsers || allUsers.items.filter(u => u.is_active).length === 0) && (
                <div className="px-4 py-3 text-sm text-gray-400 text-center">No users found</div>
              )}
            </div>
          </div>

          {testMsg && (
            <div className={`p-3 rounded-lg text-sm ${testMsg.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              {testMsg.message}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={async () => {
                setTestMsg(null)
                const tAtt = resolveChannelInput(testAttendanceInput)
                const tLv = resolveChannelInput(testLeaveInput)
                const tGl = resolveChannelInput(testGoalsInput)
                const firstError = tAtt.error || tLv.error || tGl.error
                if (firstError) {
                  setTestMsg({ success: false, message: firstError })
                  return
                }
                try {
                  await setTestUsers(Array.from(testUserIds))
                  // Auto-enable test mode if users are selected
                  const effectiveTestMode = testUserIds.size > 0 ? true : testModeEnabled
                  if (effectiveTestMode && !testModeEnabled) setTestModeEnabled(true)
                  updateTestSettingsMutation.mutate({
                    test_mode_enabled: effectiveTestMode,
                    test_attendance_channel_id: tAtt.id,
                    test_leave_channel_id: tLv.id,
                    test_goals_channel_id: tGl.id,
                  })
                  queryClient.invalidateQueries({ queryKey: ['users'] })
                } catch {
                  setTestMsg({ success: false, message: 'Failed to save test users' })
                }
              }}
              disabled={updateTestSettingsMutation.isPending}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
            >
              {updateTestSettingsMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Test Settings
            </button>
          </div>
        </div>
      </div>

      </>}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Disconnect Slack?
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              This will remove your Slack credentials and disable all Slack
              features including standups and notifications. You can reconnect
              at any time.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => disconnectMutation.mutate()}
                disabled={disconnectMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {disconnectMutation.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

